import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { selfcareApi } from '../services/SelfcareApiService';

const initialState = {
  loading: true,
  ready: false,
  error: null,
  siteInfo: null,
  isAvailable: false,
  currentLang: 'fr',
  symbol: '€',
  token: null
};

const actionTypes = {
  SET_LOADING: 'SET_LOADING',
  SET_SITE_INFO: 'SET_SITE_INFO',
  SET_ERROR: 'SET_ERROR',
  SET_LANGUAGE: 'SET_LANGUAGE',
  SET_TOKEN: 'SET_TOKEN',
  SET_READY: 'SET_READY',
  SET_SYMBOL: 'SET_SYMBOL'
};

function globalReducer(state, action) {
  switch (action.type) {
    case actionTypes.SET_LOADING:
      return { ...state, loading: action.payload };

    case actionTypes.SET_SITE_INFO:
      return {
        ...state,
        siteInfo: action.payload.siteInfo,
        isAvailable: action.payload.isAvailable,
        loading: false,
        error: null
      };

    case actionTypes.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false
      };

    case actionTypes.SET_LANGUAGE:
      return {
        ...state,
        currentLang: action.payload
      };

    case actionTypes.SET_TOKEN:
      return {
        ...state,
        token: action.payload
      };

    case actionTypes.SET_READY:
      return {
        ...state,
        ready: action.payload
      };

    case actionTypes.SET_SYMBOL:
      return {
        ...state,
        symbol: action.payload
      };

    default:
      return state;
  }
}

const GlobalContext = createContext();

let globalNavigate = null;

export function GlobalProvider({ children }) {
  const [state, dispatch] = useReducer(globalReducer, initialState);

  const setNavigate = (navigateFunction) => {
    globalNavigate = navigateFunction;
  };

  const setLoading = (loading) => {
    dispatch({ type: actionTypes.SET_LOADING, payload: loading });
  };

  const setSiteInfo = (siteInfo, isAvailable) => {
    dispatch({
      type: actionTypes.SET_SITE_INFO,
      payload: { siteInfo, isAvailable }
    });
  };

  const setError = (error) => {
    dispatch({ type: actionTypes.SET_ERROR, payload: error });
  };

  const changeLanguage = (langCode) => {
    dispatch({ type: actionTypes.SET_LANGUAGE, payload: langCode });
    localStorage.setItem('selectedLanguage', langCode);
  };

  const setToken = (token) => {
    dispatch({ type: actionTypes.SET_TOKEN, payload: token });
  };

  const setReady = (ready) => {
    dispatch({ type: actionTypes.SET_READY, payload: ready });
  };

  const setSymbol = (symbol) => {
    dispatch({ type: actionTypes.SET_SYMBOL, payload: symbol });
  };

  const handleError = (errorType) => {
    setError(errorType);
    if (globalNavigate) {
      globalNavigate(`/error?type=${errorType}`);
    } else {
      console.warn('Navigate function not available, using window.location');
      window.location.href = `/error?type=${errorType}`;
    }
  };

  function getBaseUrl() {
    const fullUrl = window.location.href;
    const url = new URL(fullUrl);
    return url.origin;
  }

  useEffect(() => {
    const initializeSite = async () => {
      try {
        setLoading(true);

        const baseURL = process.env.REACT_APP_ENV == "localhost" ? process.env.REACT_APP_BASE_URL : getBaseUrl();
        const graphics = process.env.REACT_APP_GRAPHICS_API_URL;
        const apiKey = process.env.REACT_APP_API_KEY

        const apiUrl = `${graphics}/graphics?api_key=${apiKey}&url=${baseURL}`;
        const response = await fetch(apiUrl);

        const data = await response.json();

        if (data.status === 'ok') {
          const languages = {};
          if (data.result.languages) {
            data.result.languages.forEach(language => {
              languages[language.code] = language;
            });
            data.result.languages = languages;
          }

          setSiteInfo(data.result, data.isAvailable);
          selfcareApi.setSiteInfo(data.result);

          const savedLang = localStorage.getItem('selectedLanguage');
          const defaultLang = savedLang || data.result.default_language || 'fr';
          changeLanguage(defaultLang);

          if (data.isAvailable) {
            try {
              const currencyResponse =await fetch('/assets/Common-Currency.json');
              const currencies = await currencyResponse.json();
              const symbol = currencies[data.result.devise] && currencies[data.result.devise]['symbol'] 
                ? currencies[data.result.devise]['symbol'] 
                : data.result.devise;


              window.currencySymbol = symbol;
              setSymbol(symbol);
            } catch (currencyError) {
              console.warn('Erreur lors du chargement des devises:', currencyError);
            }

            setReady(true);
          } else {
            handleError('unavailable_site');
          }
        } else {
          handleError('unknown_site');
        }
      } catch (error) {
        console.error('Erreur lors de l\'initialisation:', error);
        handleError('network_error');
      }
    };

    initializeSite();
  }, []); 

  useEffect(() => {
    const extractTokenFromPath = () => {
      const path = window.location.pathname;
      const cardsMatch = path.match(/\/cards\/(.+)/);
      if (cardsMatch) {
        return cardsMatch[1];
      }
      return null;
    };

    const extractTokenFromQuery = () => {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get('token');
    };

    const tokenFromPath = extractTokenFromPath();
    const tokenFromQuery = extractTokenFromQuery();
    const foundToken = tokenFromPath || tokenFromQuery;

    if (foundToken) {
      setToken(foundToken);
    }

    const handleLocationChange = () => {
      const newToken = extractTokenFromPath();
      if (newToken && newToken !== state.token) {
        setToken(newToken);
      } else if (!newToken && state.token) {
        setToken(null);
      }
    };

    window.addEventListener('popstate', handleLocationChange);
    
    return () => {
      window.removeEventListener('popstate', handleLocationChange);
    };
  }, [state.token]); 

  const contextValue = {
    ...state,

    setLoading,
    setSiteInfo,
    setError,
    changeLanguage,
    setToken,
    setReady,
    setSymbol,
    handleError,
    setNavigate, 

    getCurrentLanguageData: () => {
      if (!state.siteInfo?.languages?.[state.currentLang]) {
        return null;
      }
      return state.siteInfo.languages[state.currentLang];
    },

    getHomeContent: () => {
      if (!state.siteInfo?.home?.[state.currentLang]) {
        return { teaser: '', text: '' };
      }
      return state.siteInfo.home[state.currentLang];
    }
  };

  return (
    <GlobalContext.Provider value={contextValue}>
      {children}
    </GlobalContext.Provider>
  );
}

export function useGlobalContext() {
  const context = useContext(GlobalContext);
  if (!context) {
    throw new Error('useGlobalContext doit être utilisé dans un GlobalProvider');
  }
  return context;
}

export default GlobalContext;